/**
 * Utilitários para manipulação e formatação de datas
 */

/**
 * Formata uma data para exibição em formato brasileiro
 * @param {Date} date - Data a ser formatada
 * @returns {string} - Data formatada (ex: 31/12/2023)
 */
export const formatDate = (date) => {
  if (!date) return '';
  
  const d = new Date(date);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Formata uma data para formato ISO YYYY-MM-DD
 * @param {Date|string} date - Data a ser formatada
 * @returns {string} - Data formatada (ex: 2023-12-31)
 */
export const formatISO = (date) => {
  if (!date) return null;
  
  try {
    // Se for já um string no formato YYYY-MM-DD
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    
    // Se for uma data ou outro formato de string
    const d = new Date(date);
    
    // Verificar se é uma data válida
    if (isNaN(d.getTime())) {
      console.warn('Data inválida:', date);
      return null;
    }
    
    // Formatar como YYYY-MM-DD
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Erro ao formatar data ISO:', error);
    return null;
  }
};

/**
 * Formata um horário para exibição
 * @param {string} time - Horário em formato HH:MM
 * @returns {string} - Horário formatado
 */
export const formatTime = (time) => {
  if (!time) return '';
  return time;
};

/**
 * Calcula a data final somando uma duração em minutos
 * @param {Date} startDate - Data inicial
 * @param {string} startTime - Horário inicial (HH:MM)
 * @param {number} durationMinutes - Duração em minutos
 * @returns {Object} - Objeto com endTime (HH:MM) e endDate (Date)
 */
export const calculateEndTime = (startDate, startTime, durationMinutes) => {
  if (!startDate || !startTime || durationMinutes === undefined) {
    return { endTime: null, endDate: null };
  }
  
  // Criar datetime completo
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const startDateTime = new Date(startDate);
  startDateTime.setHours(startHour, startMinute, 0);
  
  // Adicionar duração
  const endDateTime = new Date(startDateTime.getTime() + durationMinutes * 60 * 1000);
  
  // Extrair hora final
  const endHour = endDateTime.getHours().toString().padStart(2, '0');
  const endMinute = endDateTime.getMinutes().toString().padStart(2, '0');
  const endTime = `${endHour}:${endMinute}`;
  
  return {
    endTime,
    endDate: endDateTime
  };
};

/**
 * Verifica se uma data é hoje
 * @param {Date} date - Data a verificar
 * @returns {boolean} - True se for hoje
 */
export const isToday = (date) => {
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
};

/**
 * Obtém o nome do dia da semana
 * @param {Date} date - Data
 * @param {boolean} short - Se true, retorna formato curto (Seg, Ter, etc)
 * @returns {string} - Nome do dia da semana
 */
export const getDayOfWeek = (date, short = false) => {
  if (!date) return '';
  
  const options = { weekday: short ? 'short' : 'long' };
  return date.toLocaleDateString('pt-BR', options);
};

/**
 * Obtém o nome do mês
 * @param {Date} date - Data
 * @param {boolean} short - Se true, retorna formato curto (Jan, Fev, etc)
 * @returns {string} - Nome do mês
 */
export const getMonthName = (date, short = false) => {
  if (!date) return '';
  
  const options = { month: short ? 'short' : 'long' };
  return date.toLocaleDateString('pt-BR', options);
};

/**
 * Converte horário em minutos desde o início do dia
 * @param {string} time - Horário no formato HH:MM
 * @returns {number} - Minutos desde o início do dia
 */
export const timeToMinutes = (time) => {
  if (!time) return 0;
  
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Converte minutos desde o início do dia em horário HH:MM
 * @param {number} minutes - Minutos desde o início do dia
 * @returns {string} - Horário no formato HH:MM
 */
export const minutesToTime = (minutes) => {
  if (minutes === undefined || minutes === null) return '';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Formata a data para exibição no padrão brasileiro (DD/MM/AAAA)
 * @param {Date|string} dateInput - Data a ser formatada
 * @returns {string} Data formatada
 */
export const formatDateBR = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    // Verificar o tipo da entrada
    if (typeof dateInput === 'string') {
      // Verificar se a data já está no formato brasileiro (DD/MM/AAAA)
      if (dateInput.includes('/')) {
        return dateInput; // Já está no formato correto
      }
      
      // Caso seja formato ISO, converter para formato brasileiro
      if (dateInput.includes('-')) {
        const parts = dateInput.split('T')[0].split('-');
        if (parts.length === 3) {
          const [year, month, day] = parts;
          return `${day}/${month}/${year}`;
        }
      }
    } else if (dateInput instanceof Date) {
      // Se for um objeto Date, formatar diretamente
      const day = String(dateInput.getDate()).padStart(2, '0');
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const year = dateInput.getFullYear();
      return `${day}/${month}/${year}`;
    }
    
    // Se não conseguiu processar, retornar a string original ou data formatada
    return typeof dateInput === 'string' 
      ? dateInput 
      : (dateInput instanceof Date ? dateInput.toLocaleDateString('pt-BR') : 'Data inválida');
  } catch (error) {
    console.error("Erro ao formatar data:", error, dateInput);
    return typeof dateInput === 'string' ? dateInput : 'Data inválida';
  }
};

/**
 * Converte data do formato brasileiro para ISO (YYYY-MM-DD)
 * @param {string} dateBR - Data no formato DD/MM/AAAA
 * @returns {string} Data no formato YYYY-MM-DD
 */
export const convertDateBRToISO = (dateBR) => {
  if (!dateBR) return '';
  
  try {
    const [day, month, year] = dateBR.split('/');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Erro ao converter data:", error);
    return dateBR;
  }
};

/**
 * Valida se a data no formato DD/MM/AAAA é válida
 * @param {string} dateBR - Data no formato DD/MM/AAAA
 * @returns {boolean} True se a data for válida
 */
export const isValidDateBR = (dateBR) => {
  // Verificar o formato básico DD/MM/AAAA
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateBR)) {
    return false;
  }
  
  // Extrair dia, mês e ano
  const [day, month, year] = dateBR.split('/').map(Number);
  
  // Verificar se o mês é válido
  if (month < 1 || month > 12) {
    return false;
  }
  
  // Verificar se o dia é válido para o mês
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) {
    return false;
  }
  
  // Verificar se o ano é razoável (por exemplo, entre 2000 e 2100)
  if (year < 2000 || year > 2100) {
    return false;
  }
  
  return true;
};

/**
 * Formata um horário garantindo que esteja no formato HH:MM
 * @param {string} horario - Horário a ser formatado
 * @returns {string} Horário formatado HH:MM
 */
export const formatarHorario = (horario) => {
  if (!horario) return '';
  // Se o horário incluir segundos, remova-os
  return horario.substring(0, 5); // Pega apenas hh:mm
};

/**
 * Converte duração em minutos para o formato HH:MM
 * @param {number} minutes - Duração em minutos
 * @returns {string} Duração no formato HH:MM
 */
export const convertMinutesToTimeFormat = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Converte duração do formato HH:MM para minutos
 * @param {string} timeString - Duração no formato HH:MM
 * @returns {number} Duração em minutos
 */
export const convertTimeFormatToMinutes = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return (hours * 60) + minutes;
};

/**
 * Formata a data para exibição no padrão brasileiro estendido (Dia da semana, DD de Mês de AAAA)
 * @param {Date|string} dateInput - Data a ser formatada
 * @returns {string} Data formatada com nome do dia da semana, dia, mês por extenso e ano
 */
export const formatDateExtended = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    // Corrigindo problema de fuso horário
    let date;
    
    if (typeof dateInput === 'string') {
      // Se for string, processar adequadamente
      if (dateInput.includes('T')) {
        // Se já tiver tempo, usar diretamente
        date = new Date(dateInput);
      } else {
        // Adicionando um horário zero para evitar conversão de fuso horário
        date = new Date(`${dateInput}T00:00:00`);
      }
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      console.error("Tipo de data inválido:", typeof dateInput);
      return '';
    }
    
    // Verificar se a data é válida
    if (isNaN(date.getTime())) {
      console.error("Data inválida:", dateInput);
      return '';
    }
      
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  } catch (error) {
    console.error("Erro ao formatar data estendida:", error, dateInput);
    return '';
  }
};

/**
 * Formata um tempo relativo (distância entre datas)
 * @param {Date|string} dateInput - Data futura a ser comparada com agora
 * @returns {string} Texto formatado para a distância de tempo (ex: "em 2 dias", "em 3 horas")
 */
export const formatRelativeTime = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    // Converter a entrada para objeto Date
    let futureDate;
    if (typeof dateInput === 'string') {
      futureDate = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      futureDate = dateInput;
    } else {
      return '';
    }
    
    // Verificar se a data é válida
    if (isNaN(futureDate.getTime())) {
      return '';
    }
    
    // Obter a data atual
    const now = new Date();
    
    // Calcular a diferença em milissegundos
    const diffMs = futureDate.getTime() - now.getTime();
    
    // Converter para unidades de tempo
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    // Formatar o texto
    if (diffDays > 0) {
      return `em ${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`;
    } else if (diffHours > 0) {
      return `em ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
    } else if (diffMinutes > 0) {
      return `em ${diffMinutes} ${diffMinutes === 1 ? 'minuto' : 'minutos'}`;
    } else {
      return 'agora';
    }
  } catch (error) {
    console.error("Erro ao formatar tempo relativo:", error);
    return '';
  }
};

/**
 * Verifica se duas datas representam o mesmo dia
 * @param {Date} date1 - Primeira data
 * @param {Date} date2 - Segunda data
 * @returns {boolean} - True se as datas representam o mesmo dia
 */
export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  
  // Garantir que sejam objetos Date
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  
  return d1.getDate() === d2.getDate() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getFullYear() === d2.getFullYear();
}; 